import { useState } from "react";
import { toast } from "react-toastify";
import api from "../../../utils/api";
import { useRestaurantConfig } from "../../../utils/restaurantConfig";
import {
  INITIAL_CUSTOMER_STATE,
  API_ENDPOINTS,
  MESSAGES,
  VALIDATION,
} from "../constants";
import { buildOrderPayload } from "../utils";

/**
 * Custom hook for managing takeaway order logic
 * Handles customer info, products, calculations, and order submission
 */
export const useTakeawayOrder = () => {
  const [loading, setLoading] = useState(false);
  const { restaurantId, gstFraction } = useRestaurantConfig();
  const gstPercent = gstFraction * 100;

  const [customer, setCustomer] = useState(INITIAL_CUSTOMER_STATE);
  const [products, setProducts] = useState([]);

  /**
   * Add product to order or increase quantity if already exists
   */
  const handleAddProduct = (prod) => {
    const exists = products.find((p) => p._id === prod._id);

    if (exists) {
      setProducts((prev) =>
        prev.map((p) =>
          p._id === prod._id ? { ...p, quantity: p.quantity + 1 } : p,
        ),
      );
      toast.success(MESSAGES.QUANTITY_INCREASED(prod.productName));
    } else {
      setProducts([
        ...products,
        {
          ...prod,
          quantity: 1,
          price: prod.price,
          offerPrice: prod.offerPrice,
          finalPrice: prod.offerPrice || prod.price,
          imageUrl: prod.imageUrl || prod.image || "",
        },
      ]);
      toast.success(MESSAGES.PRODUCT_ADDED(prod.productName));
    }
  };

  /**
   * Update product quantity
   */
  const updateQuantity = (id, qty) => {
    setProducts((prev) =>
      prev.map((p) => (p._id === id ? { ...p, quantity: qty } : p)),
    );
  };

  /**
   * Remove product from order
   */
  const removeProduct = (id) => {
    setProducts((prev) => prev.filter((p) => p._id !== id));
    toast.info(MESSAGES.PRODUCT_REMOVED);
  };

  /**
   * Calculate order totals
   */
  const calculations = {
    subtotal: products.reduce((sum, p) => {
      const price = Number(p.finalPrice || p.offerPrice || p.price || 0);
      return sum + p.quantity * price;
    }, 0),

    totalPackageCharge: products.reduce((sum, p) => {
      const charge = Number(p.packageCharge || 0);
      return sum + p.quantity * charge;
    }, 0),

    get gstAmount() {
      return this.subtotal * gstFraction;
    },

    get grandTotal() {
      return this.subtotal + this.gstAmount + this.totalPackageCharge;
    },
  };

  /**
   * Clear all order data
   */
  const handleCancel = () => {
    setCustomer(INITIAL_CUSTOMER_STATE);
    setProducts([]);
    toast.info(MESSAGES.ORDER_CLEARED);
  };

  /**
   * Validate and submit order
   */
  const handleSubmit = async () => {
    // Validation
    if (products.length === 0) {
      toast.error(MESSAGES.NO_PRODUCTS);
      return;
    }

    if (products.some((p) => p.quantity <= 0)) {
      toast.error(MESSAGES.INVALID_QUANTITY);
      return;
    }

    // if (!customer.name.trim()) {
    //   toast.error(MESSAGES.NAME_REQUIRED);
    //   return;
    // }

    // if (!customer.phone.trim()) {
    //   toast.error(MESSAGES.PHONE_REQUIRED);
    //   return;
    // }

    // Build payload
    const payload = buildOrderPayload({
      customer,
      products,
      calculations,
      restaurantId,
    });

    // Submit order
    setLoading(true);
    try {
      const response = await api.post(API_ENDPOINTS.CREATE_ORDER, payload);

      if (response.status === 201) {
        toast.success(MESSAGES.ORDER_SUCCESS);
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (error) {
      console.error("Error creating order:", error.response?.data);
      toast.error(error.response?.data?.message || MESSAGES.ORDER_FAILED);
    } finally {
      setLoading(false);
    }
  };

  return {
    // State
    loading,
    customer,
    setCustomer,
    products,
    gstPercent,

    // Calculations
    ...calculations,

    // Handlers
    handleAddProduct,
    updateQuantity,
    removeProduct,
    handleCancel,
    handleSubmit,

    // Validation
   isFormValid: products.length > 0,
  };
};
